import { chakra, Box, Flex, FlexProps, Icon, List, ListItem, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useCombobox } from 'downshift';
import { FC, useState } from 'react';
import { InView } from 'react-intersection-observer';

import CheckMarkIcon from '@/assets/icons/ic-check-mark.svg';

import { FilterComboboxInput } from '@/components/ui/features/library/LibraryFilter/components/FilterComboboxInput';
import { FilterComboboxItem } from '@/components/ui/features/library/LibraryFilter/components/FilterComboboxItem';
import { IComboboxFilterItemProps, IOmnisearchFilterItem } from '@/interfaces/search';

interface ICombobox extends FlexProps {
  itemsList: Array<IOmnisearchFilterItem>;
  placeholder?: string;
  isLoadingMore?: boolean;
  defaultItem: IOmnisearchFilterItem;
  onScrollToBottom?: () => void;
  onChangeItem(item: IOmnisearchFilterItem): void;
  onSearchChange(inputValue: string): void;

  getItem: (item: IOmnisearchFilterItem) => IComboboxFilterItemProps;
}

export const FilterCombobox: FC<ICombobox> = ({
  onChangeItem,
  defaultItem,
  onSearchChange,
  onScrollToBottom,
  placeholder,
  isLoadingMore,
  itemsList,
  getItem,
}) => {
  const [selectedValue, setSelectedValue] = useState(defaultItem.value || undefined);
  const itemsWithoutSelected = itemsList.filter((it) => it.value !== selectedValue);
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    selectedItem = defaultItem,
    setInputValue,
    getItemProps,
  } = useCombobox({
    isOpen: true,
    items: itemsWithoutSelected,
    itemToString(item) {
      return item ? item.label : '';
    },
    onInputValueChange: ({ inputValue }) => {
      onSearchChange(inputValue);
    },
    onSelectedItemChange: ({ selectedItem: item }) => {
      console.log('selected item change');
      setInputValue('');
      onChangeItem(item);
      setSelectedValue(item.value);
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          return {
            ...changes,
            highlightedIndex: 0,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.InputFocus:
          return {
            ...changes,
            highlightedIndex: 0,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
  });

  const selected = selectedItem || defaultItem;

  return (
    <Flex direction="column" flex="1 1 auto" borderRadius="2px">
      <Flex direction="row" alignItems="baseline">
        <FilterComboboxInput {...getInputProps()} placeholder={placeholder} />
      </Flex>
      {selected && (
        <Flex w="100%" my="3" justify="space-between">
          <Text fontWeight="semibold">{selected?.label}</Text>
          <Box>
            <Icon as={CheckMarkIcon} color="violet.400" ml="auto" />
          </Box>
        </Flex>
      )}
      <chakra.ul
        {...getMenuProps()}
        flex={1}
        display={isOpen ? 'block' : 'none'}
        overflowY="auto"
        maxH={{ base: '350', md: '300', '2xl': '415' }}
      >
        {itemsWithoutSelected.map((item, index) => {
          const formattedItem = getItem(item);

          return (
            <FilterComboboxItem
              as="li"
              key={item.value}
              item={formattedItem}
              {...getItemProps({ item, index })}
              isHighlighted={index == highlightedIndex}
            />
          );
        })}
        {isLoadingMore ? <ItemLoadingFallback /> : null}
        <InView
          as="span"
          skip={!onScrollToBottom}
          rootMargin="20px 0px 0px 0px"
          onChange={(inView) => inView && onScrollToBottom?.()}
        />
      </chakra.ul>
    </Flex>
  );
};

function ItemLoadingFallback() {
  return (
    <VStack spacing={6}>
      <Box px={4} w="full">
        <Skeleton h="24px" w="full" />
      </Box>
      <Box px={4} w="full">
        <Skeleton h="24px" w="full" />
      </Box>
      <Box px={4} w="full">
        <Skeleton h="24px" w="full" />
      </Box>
    </VStack>
  );
}
